/**
 * Flow-field maintenance.
 *
 * A Dijkstra over the whole map is the most expensive single thing the
 * simulation does, so the rule here is: rebuild a field only when somebody is
 * actually going to read it, and only when the world it describes has changed.
 *
 *   flowMain  — the horde's route to the Hearth. Rebuilt when the navigable
 *               world changes, and on a slow heartbeat during a siege so that
 *               accumulating wall damage re-weights the approach.
 *   flowEcon  — raiders' route to the outlying producers. Only built when a
 *               raider company actually exists.
 *   flowHome  — defenders' route home. Only rebuilt on structural change; it
 *               does not care about wall *damage*, only wall *presence*.
 */

import { alliedWith, areHostile, Team } from '../types.ts';
import type { World } from '../world.ts';
import { buildingType } from '../../data/buildings.ts';
import { Phase } from '../types.ts';

/**
 * How often the besiegers re-read the walls, in ticks — and only when a wall
 * has actually been hit since the last read. See `World.dirty.flowWeights`.
 */
const SIEGE_REFRESH = 90;

/**
 * Tiles a sliced rebuild may expand per tick, per field in flight.
 *
 * Chosen against the measured cost of a tile — a sweep of a 384×384 map settles
 * about a hundred and fifty thousand of them in a couple of hundred
 * milliseconds — so four thousand is a handful of milliseconds of work and a
 * full rebuild lands in something over a second. That is the right trade: the
 * horde noticing a fresh breach a second late is invisible, and a
 * two-hundred-millisecond hitch every time a hut falls is not.
 *
 * A *count*, never a time budget. Two runs of the same seed must slice at
 * exactly the same points, and a wall clock cannot promise that.
 */
const FLOW_BUDGET = 4000;

export function maybeRebuildFlows(w: World): void {
  // The villagers' route field is owed its slice whatever the horde's fields
  // are doing. It is driven from here rather than from `PathCache.get` because
  // a rebuild has to make progress on ticks where nobody happens to ask.
  w.paths.advance(w.map, FLOW_BUDGET);

  // A rebuild already in flight gets its slice; nothing new is started until it
  // has published, so the fields never disagree about which world they describe.
  if (w.flowMain.rebuilding || w.flowEcon.rebuilding || w.flowHome.rebuilding) {
    sliceFlows(w, FLOW_BUDGET);
    return;
  }

  const structuralChange = w.dirty.flow || w.flowMain.stamp < 0;
  const heartbeat =
    w.phase === Phase.Siege && w.dirty.flowWeights && w.tick - w.flowMain.stamp >= SIEGE_REFRESH;
  if (!structuralChange && !heartbeat) return;

  // The very first build has no previous field to steer by, so it lands whole.
  // Every later one is sliced.
  const cold = w.flowMain.stamp < 0;
  beginFlows(w, structuralChange);
  sliceFlows(w, cold ? Number.POSITIVE_INFINITY : FLOW_BUDGET);
}

/** Build every field to completion now. Used by the tests and by a cold start. */
export function rebuildFlows(w: World, structural = true): void {
  beginFlows(w, structural);
  sliceFlows(w, Number.POSITIVE_INFINITY);
}

/**
 * Two alliances, two fields — and that is the whole answer to "does 2v2 cost
 * four Dijkstras".
 *
 * It does not, and the reason is that a flow field is keyed on *where it goes*
 * and *whose stonework is in the way*, not on who walks it. Both foe banners
 * want the Hearth and both treat the same walls as an obstacle to smash; both
 * home banners want to get behind those walls and treat them as a thing to go
 * round. So `flowMain` serves `Alliance.Foe` entire and `flowHome` serves
 * `Alliance.Home` entire, exactly as they did when each alliance had one member.
 * The team ids passed here are the *representative* of each alliance, and every
 * ownership test inside `flow.ts` is `alliedWith` rather than `===`, which is
 * what makes that true rather than merely convenient.
 */
function beginFlows(w: World, structural: boolean): void {
  const teamOf = (b: number) => w.buildings.team[b];
  const map = w.map;
  const hearthGoals = hearthRing(w);
  const scatter = w.obstacleGrid;

  /**
   * Which side is outside the walls, and which is behind them.
   *
   * `Team.Invader` and `Team.Player` were written here as constants, which is
   * true of every settlement and false of an assault field, where the walls
   * belong to the defender and the player is the besieger. Both fields are
   * named off `w.wallTeam` instead: the one that smashes through is whoever is
   * hostile to the stonework, and the one that routes around it and through its
   * gates is whoever owns it. In a settlement `w.wallTeam` is `Team.Player` and
   * these are the two constants they replace, exactly.
   */
  const holder = w.wallTeam;
  const besieger = alliedWith(holder, Team.Player) ? Team.Invader : Team.Player;

  w.flowMain.begin(map, hearthGoals, besieger, teamOf, true, scatter);

  // Raider field: only worth the Dijkstra if somebody is going to walk it.
  const wantsEcon = w.companies.some((c) => areHostile(c.team, holder) && !c.dead && c.flowKind === 1);
  if (wantsEcon) {
    const econGoals: number[] = [];
    const b = w.buildings;
    for (let i = 0; i < b.slots.high; i++) {
      if (b.slots.alive[i] === 0) continue;
      if (!alliedWith(b.team[i], holder)) continue;
      const def = buildingType(b.typeId[i]);
      if (def.category !== 'economy') continue;
      const x = b.tx[i] - 1;
      const y = b.ty[i] - 1;
      if (map.inBounds(x, y)) econGoals.push(map.idx(x, y));
    }
    w.flowEcon.begin(map, econGoals.length > 0 ? econGoals : hearthGoals, besieger, teamOf, true, scatter);
  }

  // The defenders' route home only changes when the town's shape changes.
  if (structural || w.flowHome.stamp < 0) {
    w.flowHome.begin(map, hearthGoals, holder, teamOf, false, scatter);
    // Villager routes are only valid for the shape of the world that produced
    // them, and that shape has just changed. The path cache keeps serving the
    // old routes until its own field has been rebuilt — see `PathCache.clear`.
    w.paths.clear();
  }

  // `dirty.flow` is cleared here rather than when the last field publishes: the
  // world's shape as of *now* is what the sweep in flight was started against,
  // and a change that lands while it runs must set the flag again.
  w.dirty.flow = false;
  w.dirty.flowWeights = false;
}

function sliceFlows(w: World, budget: number): void {
  if (w.flowMain.rebuilding && w.flowMain.advance(budget)) w.flowMain.stamp = w.tick;
  if (w.flowEcon.rebuilding && w.flowEcon.advance(budget)) w.flowEcon.stamp = w.tick;
  if (w.flowHome.rebuilding && w.flowHome.advance(budget)) w.flowHome.stamp = w.tick;
}

/** Ring of tiles around the Hearth footprint: the goal is to *reach* it. */
function hearthRing(w: World): number[] {
  const map = w.map;
  const goals: number[] = [];
  if (w.hearth >= 0 && w.buildings.isAlive(w.hearth)) {
    const def = buildingType(w.buildings.typeId[w.hearth]);
    const tx = w.buildings.tx[w.hearth];
    const ty = w.buildings.ty[w.hearth];
    for (let dy = -1; dy <= def.h; dy++) {
      for (let dx = -1; dx <= def.w; dx++) {
        const onRing = dx === -1 || dy === -1 || dx === def.w || dy === def.h;
        if (!onRing) continue;
        const x = tx + dx;
        const y = ty + dy;
        if (!map.inBounds(x, y)) continue;
        goals.push(map.idx(x, y));
      }
    }
  }
  if (goals.length === 0) goals.push(map.idx(map.hearthX, map.hearthY));
  return goals;
}
